import { useEffect } from 'react';

/**
 * Mirrors the original site.js scroll-reveal: elements with `.reveal` fade/slide
 * in when they enter the viewport. Honors prefers-reduced-motion (reveals
 * everything immediately). Call once near the top of the landing page.
 */
export function useScrollReveal() {
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const reveals = Array.from(document.querySelectorAll('.landing-v2 .reveal'));

    if (reduce || !('IntersectionObserver' in window)) {
      reveals.forEach((el) => el.classList.add('in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    reveals.forEach((el) => {
      if (!el.classList.contains('in')) io.observe(el);
    });

    return () => io.disconnect();
  }, []);
}

/**
 * Sticky-header shadow toggle: adds `.scrolled` to the given element once the
 * page is scrolled past a small threshold.
 */
export function useStickyHeaderShadow(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      if (window.scrollY > 8) el.classList.add('scrolled');
      else el.classList.remove('scrolled');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [ref]);
}

/**
 * Smooth-scroll to an in-page anchor with a sticky-header offset, matching the
 * original site.js behavior (84px offset, respects reduced-motion).
 */
export function scrollToAnchor(hash: string) {
  if (hash.length < 2) return;
  const target = document.querySelector(hash);
  if (!target) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const top = target.getBoundingClientRect().top + window.scrollY - 84;
  window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
}
